import { jwtDecode } from "jwt-decode";
import CryptoJS from 'crypto-js';
import { canPost, type AccessPolicy, type PostingAction } from './access';
import { toast } from "react-toastify";
import { useLocation } from "react-router-dom";
import { Dispatch, SetStateAction } from "react";
import {t} from "i18next";
import { compareToBusinessToday, parseISODay } from "./businessClock";


export class Util {

	static hashObject = (obj: any) => {
		return CryptoJS.SHA256(obj).toString();
	};

	static getLongNumber(): string {
		return new Date().toISOString().replace(/[-:.TZ]/g, '');
	}

	static getDepositNumber(): string {
		return `D${Util.getLongNumber()}`;
	}

	static getInvoiceNumber(): string {
		return `I${Util.getLongNumber()}`;
	}

	static getDocumentNumber(): string {
		return `O${Util.getLongNumber()}`;
	}

	static getMoveNumber(): string {
		return `M${Util.getLongNumber()}`;
	}

	static getSupplyNumber(): string {
		return `S${Util.getLongNumber()}`;
	}

	static getConversionNumber(): string {
		return `C${Util.getLongNumber()}`;
	}

	static getProductionNumber(): string {
		return `P${Util.getLongNumber()}`;
	}

	static getInventoryNumber(): string {
		return `INV${Util.getLongNumber()}`;
	}

	static getLocale(): string {
		let locale = localStorage.getItem("i18nextLng") || "en";
		return this.compareStrings(locale, "fr") ? "fr-FR" : "en-US";
	}

	static formatDate(value: string, style: "full" | "long" | "medium" | "short") {
		// Date-only strings are calendar days: parse them locally, not as UTC midnight.
		const date = parseISODay(value) ?? new Date(value);
		return date.toLocaleDateString(this.getLocale(), { dateStyle: style });
	}

	static formatISODate(value: Date) {
		return value.toLocaleDateString("sv-SE");
	}

	static formatNumber(value: number) {
		// Quantities carry 4 decimals (QTY_SCALE); Intl's default of 3 would cut 0.3333 to 0.333.
		return new Intl.NumberFormat(this.getLocale(), { maximumFractionDigits: 4 }).format(value);
	}

	static formatCurrency(value: number, currency: string = "USD") {
		// A missing amount must never print as "NaN".
		const amount = Number.isFinite(Number(value)) ? Number(value) : 0;
		try {
			return new Intl.NumberFormat(this.getLocale(), { style: 'currency', currency }).format(amount);
		} catch (error) {
			// Intl only knows 3-letter ISO codes, so a local label like "FCFA"
			// throws; print the number and put the label after it.
			const formatted = new Intl.NumberFormat(this.getLocale(), {
				minimumFractionDigits: 2,
				maximumFractionDigits: 2,
			}).format(amount);
			const label = String(currency || "").trim();
			return label ? `${formatted} ${label}` : formatted;
		}
	}

	static buildUrl(baseUrl: string, path: string, params: any = {}) {
		const url = new URL(path, baseUrl);
		Object.keys(params).forEach(key => url.searchParams.append(key, params[key]));
		return url.toString();
	}

	static tokenExpired(msg: string): boolean {
		return Util.compareStrings(msg, "Token has expired");
	}

	static compareStrings = (str1: string, str2: string): boolean => {
		return str1.localeCompare(str2, undefined, { sensitivity: 'base' }) === 0;
	}

	static isBoolSettingEnabled = (settingValue: string | undefined, defaultEnabled = false): boolean => {
		const value = (settingValue ?? (defaultEnabled ? "True" : "False")).trim();
		return ["1", "true", "yes", "on"].includes(value.toLowerCase());
	}

	static isSessionValid = (session: any): any => {
		if (!session) {
			return null;
		}

		const { access_token } = session;
		if (!access_token) {
			return null;
		}

		try {
			const decoded = jwtDecode<{ exp?: number }>(access_token);
			// Treat expired tokens as invalid so multi-tab bootstrap cannot revive them.
			if (typeof decoded.exp === 'number' && decoded.exp * 1000 <= Date.now()) {
				return null;
			}
			return decoded;
		} catch (error) {
			console.error("Error decoding token:", error);
			return null;
		};
	}

	/** True when the JWT can still be used (not expired, with small clock skew). */
	static isAccessTokenUsable = (token: string | null | undefined, skewMs = 5000): boolean => {
		if (!token) return false;
		try {
			const decoded = jwtDecode<{ exp?: number }>(token);
			if (typeof decoded.exp !== 'number') return true;
			return decoded.exp * 1000 > Date.now() + skewMs;
		} catch {
			return false;
		}
	}

	/**
	 * Mirrors backend `_validate_dating`: a date in the past needs the posting's
	 * `pre_date` sub under the verb being performed, a date in the future its
	 * `post_date` sub. Compares YYYY-MM-DD strings against the server's business
	 * "today", so the browser's timezone or clock never shifts the answer.
	 */
	static checkDating = (
		date: string,
		access: AccessPolicy | null | undefined,
		scope: DatingScope,
		action: PostingAction = "create",
	): boolean => {
		const position = compareToBusinessToday(date);

		if (position < 0 && !canPost(access, scope, action, "pre_date")) {
			toast.error(t("common:toast.error.noPredating"), { autoClose: 2000 });
			return false;
		} else if (position > 0 && !canPost(access, scope, action, "post_date")) {
			toast.error(t("common:toast.error.noPostdating"), { autoClose: 2000 });
			return false;
		}

		return true;
	}

	static stringToColor = (str: string): string => {
		let hash = 0;
		
		for (let i = 0; i < str.length; i++) {
			hash = str.charCodeAt(i) + ((hash << 5) - hash);
		}

		// Generate hue (0–360)
		const hue = Math.abs(hash % 360);

		// Use HSL for vibrant, consistent colors
		return `hsl(${hue}, 70%, 50%)`;
	};

}

export const updateActiveTask = <T>(
	setter: Dispatch<SetStateAction<T[]>>,
	updates: Partial<T> | ((prev: T) => Partial<T>)
) => {
	setter(prevTasks =>
		prevTasks.map(task => {
			if (!(task as any).active) return task;

			const computedUpdates =
				typeof updates === "function" ? updates(task) : updates;

			return {
				...task,
				...computedUpdates,
				updatedAt: new Date(),
			};
		})
	);
};

export const getValidId = <T extends { id: number }>(list: T[], currentId?: number) =>
	Number(list.find(item => item.id === currentId)?.id || list[0]?.id || -1);

export const getId = <T extends { id: number }>(list: T[], currentId?: number): number =>
	Number((currentId || -1) > 0 ? (currentId || NaN) : (list[0]?.id || NaN));

export const isValidId = <T extends { id: number }>(
	list: T[],
	currentId?: number
) => list.some(item => item.id === currentId);

/** Postings that carry the two dating subs. */
export type DatingScope =
	| 'sale'
	| 'payment'
	| 'conversion'
	| 'production'
	| 'supply'
	| 'movement'
	| 'offer'
	| 'expense'
	| 'voucher'
	| 'wallet'
	| 'deposit'
	| 'supply_payment';

/**
 * Say no, out loud.
 *
 * The grant helpers in util/access are pure predicates, so the message lives here:
 * `if (!allowOrWarn(canUpdate(access, "item"))) return;`
 *
 * Only for a refusal the user caused -- a click on something they may not do.
 * Hiding a control needs no toast.
 */
export const allowOrWarn = (allowed: boolean): boolean => {
	if (!allowed) {
		toast.error(t('common:toast.error.permDenied'), { autoClose: 2000 });
	}
	return allowed;
};

// Reusable filter factory: prefix matches first, then substring matches.
// Single pass with one lowercase per option — this runs on every keystroke over
// the whole catalog, which is thousands of items.
export const createFilterOptions =
	(getLabel: (opt: any) => string) =>
		(options: any[], { inputValue }: { inputValue: string }) => {
			if (!inputValue) return options;

			const lowerInput = inputValue.toLowerCase();

			const beginsWith: any[] = [];
			const contains: any[] = [];

			for (const opt of options) {
				const label = getLabel(opt).toLowerCase();
				const at = label.indexOf(lowerInput);

				if (at === 0) {
					beginsWith.push(opt);
				} else if (at > 0) {
					contains.push(opt);
				}
			}

			return beginsWith.concat(contains);
		};


export const useIsListing = () => {
	const { pathname } = useLocation();
	return pathname.toLowerCase().endsWith("/list");
};

interface DecodedToken {
	exp: number;
}

export function getTokenRemainingTime(token: string): number {
	const decoded = jwtDecode<DecodedToken>(token);
	const currentTime = Math.floor(Date.now() / 1000);
	return decoded.exp - currentTime; // in seconds
}

/**
 * Adaptive keep-alive window relative to configured session lifetime.
 * For a 60s timeout this is ~30s — plenty of time to slide while the user works.
 * For long sessions it caps so we don't hammer /check_session early.
 */
export function getKeepAliveThresholdSeconds(lifetimeSeconds?: number): number {
	const life =
		typeof lifetimeSeconds === 'number' && lifetimeSeconds > 0
			? lifetimeSeconds
			: 300;
	// ~50% of lifetime, min 20s, max 180s; always leave a little headroom after slide.
	const half = Math.floor(life * 0.5);
	return Math.min(180, Math.max(20, half));
}

export function isTokenExpiringSoon(
	token: string,
	thresholdSeconds?: number,
	lifetimeSeconds?: number
): boolean {
	const remaining = getTokenRemainingTime(token);
	const thr =
		typeof thresholdSeconds === 'number'
			? thresholdSeconds
			: getKeepAliveThresholdSeconds(lifetimeSeconds);
	return remaining > 0 && remaining <= thr;
}